<script lang="ts" setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useDisplay } from 'vuetify';
import usePlaylistStore from '../../stores/playlist';
import useNavigationStructureStore from '../../stores/navigation-structure';
import useUiStore from '../../stores/ui';
import useNavigationTriggerStore from '../../stores/navigation-trigger';
import useLocale from '../../composables/useLocale';
import type { GoToItem } from '../../types';
import type { PlaylistItemWithTimings } from '../../stores/playlist';

type Emits = { (e: 'goToContent', item: GoToItem): void };
const emit = defineEmits<Emits>();

type Props = {
  item: PlaylistItemWithTimings;
  index: number;
};
const props = defineProps<Props>();

const { t } = useLocale();
const { setNavigationTrigger } = useNavigationTriggerStore();
const { currentPlaylistIndex } = storeToRefs(usePlaylistStore());
const navigationStructureStore = useNavigationStructureStore();
const { getChapterTitleForHref, getPageByHref } = navigationStructureStore;
const { toggleTableOfContentDrawer } = useUiStore();
const { smAndDown } = useDisplay();

const isActive = computed(() => {
  return currentPlaylistIndex.value === props.index;
});

/**
 * Format duration in seconds to human-readable format
 * - 0 seconds: "" (empty string)
 * - Less than 60 seconds: "45 sec"
 * - 1-59 minutes: "15 min 30 sec"
 * - 60 minutes or more: "3 h 2 min" (no seconds)
 */
const formatDuration = (seconds: number): string => {
  const totalSeconds = Math.floor(seconds);

  // Return an empty string for 0 seconds
  if (totalSeconds === 0) {
    return '';
  }

  // Less than 60 seconds: just show seconds
  if (totalSeconds < 60) {
    return `${totalSeconds} ${t('book_player.label_duration_seconds_short')}`;
  }

  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;

  // Less than 60 minutes: show minutes and seconds
  if (mins < 60) {
    return `${mins} ${t('book_player.label_duration_minutes_short')} ${secs} ${t(
      'book_player.label_duration_seconds_short',
    )}`;
  }

  // 60 minutes or more: show hours and remaining minutes (no seconds)
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  return `${hours} ${t('book_player.label_duration_hours_short')} ${remainingMins} ${t(
    'book_player.label_duration_minutes_short',
  )}`;
};

const tocTitle = computed(() => {
  const title = getChapterTitleForHref(props.item.href);
  return title || null;
});

const pageNumber = computed(() => {
  const cleanHref = props.item.href.split('#')[0];

  // Try to find page by href using the store method
  const page = getPageByHref(cleanHref) || getPageByHref(props.item.href);
  return page?.page || null;
});

const segmentTitle = computed(() => {
  return `${t('book_player.label_segment', { n: props.index + 1 })}`;
});

const goToPlaylistItem = () => {
  if (smAndDown.value) {
    toggleTableOfContentDrawer(false);
  }

  setNavigationTrigger('PLAYLIST');
  emit('goToContent', {
    target: props.index,
    isGoingForwardsInBook: props.index > currentPlaylistIndex.value,
  });
};
</script>

<template>
  <div class="playlist-item">
    <v-list-item
      class="px-2 py-3"
      link
      color="primary"
      :active="isActive"
      :data-testid="`bp-playlist-item-${index}`"
      @click.stop="goToPlaylistItem"
    >
      <div class="title-row">
        <v-list-item-title class="segment-title">
          {{ segmentTitle }}
        </v-list-item-title>
        <v-list-item-title v-if="tocTitle" class="toc-title">
          {{ tocTitle }}
        </v-list-item-title>
      </div>

      <div class="info-row">
        <v-list-item-subtitle class="duration">
          {{ formatDuration(item.duration) }}
        </v-list-item-subtitle>
        <v-list-item-subtitle v-if="pageNumber" class="page-info">
          {{ t('book_player.label_page') }} {{ pageNumber }}
        </v-list-item-subtitle>
      </div>
    </v-list-item>
  </div>
</template>

<style scoped lang="scss">
.title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 4px;
}

.segment-title {
  flex-shrink: 0;
  white-space: nowrap;
}

.toc-title {
  flex: 1;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: normal;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.duration {
  flex-shrink: 0;
  font-size: 0.875rem;
  white-space: nowrap;
}

.page-info {
  flex-shrink: 0;
  font-size: 0.875rem;
  white-space: nowrap;
  text-align: right;
}
</style>
